'use client';

import { MetaDefault } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { formatNumber } from '../../utils/formatNumber';
import { DashboardStripCard } from './DashboardStripCard';
import type { HomeDestination } from './home-destination';
import {
  buildSnapshotDeltaCells,
  hasSnapshotBaseline,
  type SnapshotDelta,
} from './snapshot-delta-cells';
import { StripMetricCell } from './StripMetricCell';
import { useOpenHomeDestination } from './useOpenHomeDestination';

export type SnapshotStripCell = {
  key: string;
  label: string;
  icon: ReactNode;
  current: number;
  previous: number | undefined;
  destination?: HomeDestination;
};

type SnapshotSinceLastVisitStripProps = {
  applicationId: string;
  cells: SnapshotStripCell[];
  isLoading: boolean;
};

export function SnapshotSinceLastVisitStrip({
  applicationId,
  cells,
  isLoading,
}: SnapshotSinceLastVisitStripProps) {
  const t = useTranslations('home.sinceLastVisit');
  const { openDestination, openDestinationContextMenu } =
    useOpenHomeDestination({ applicationId });
  const hasBaseline = hasSnapshotBaseline(cells);
  const deltas = new Map(
    buildSnapshotDeltaCells(cells).map((cell) => [cell.key, cell.delta]),
  );

  return (
    <DashboardStripCard
      title={t('title')}
      trailingContent={
        !isLoading && !hasBaseline ? (
          <MetaDefault color="fg.neutral.muted">{t('noBaseline')}</MetaDefault>
        ) : null
      }
      columns={cells.length >= 5 ? 5 : 4}
      cells={cells.map((cell) => {
        const { destination } = cell;

        return {
          key: cell.key,
          onClick: destination ? () => openDestination(destination) : undefined,
          onContextMenu: destination
            ? (event) => openDestinationContextMenu(event, destination)
            : undefined,
          content: (
            <StripMetricCell
              label={cell.label}
              icon={cell.icon}
              value={cell.current}
              isLoading={isLoading}
              secondary={
                <SnapshotDeltaLabel
                  delta={deltas.get(cell.key) ?? { kind: 'none' }}
                />
              }
            />
          ),
        };
      })}
    />
  );
}

function SnapshotDeltaLabel({ delta }: { delta: SnapshotDelta }) {
  const t = useTranslations('home.sinceLastVisit');

  switch (delta.kind) {
    case 'none':
      return null;
    case 'unchanged':
      return (
        <MetaDefault color="fg.neutral.subtle">
          {t('deltaUnchanged')}
        </MetaDefault>
      );
    case 'up':
      return (
        <MetaDefault color="fg.neutral.default">
          {t('deltaUp', { count: formatNumber(delta.amount) })}
        </MetaDefault>
      );
    case 'down':
      return (
        <MetaDefault color="fg.neutral.muted">
          {t('deltaDown', { count: formatNumber(delta.amount) })}
        </MetaDefault>
      );
  }
}
